import { FrontendRestaurant } from '../restaurant/restaurantService';
import { getUserDefaults, setUserDefault } from './userDefaultService';
import {
  useAppUserDefaultStore,
  ALL_RESTAURANTS_ID,
  SHOW_EX_VAT_KEY,
  SHOW_EX_VAT_ORDERS_KEY,
  APP_PAGES_EXPANDED_KEY,
  LEAKAGE_QTY_UNIT_KEY,
  parseLeakageQtyUnit,
  createAllRestaurantsOption
} from '../../stores/appUserDefaultStore';
import { isImpersonating, getImpersonationRestaurantId } from '../../stores/appImpersonationStore';

const RESTAURANT_KEY = 'selected_restaurant_id';

/**
 * Load all initial user defaults needed on app startup in a single batch call.
 * This includes:
 * - Selected restaurant
 * - Show ex-VAT revenue setting
 * - Show ex-VAT orders setting
 * - Leakage tab quantity-unit setting
 */
export async function loadInitialUserDefaults(
  restaurants: FrontendRestaurant[],
  userId: string
): Promise<void> {
  if (!restaurants.length) return;

  const store = useAppUserDefaultStore.getState();

  // Skip if already loading or loaded
  if (store.isLoading || store.isLoaded) {
    return;
  }

  // Set loading state
  useAppUserDefaultStore.setState({ isLoading: true });

  try {
    // Batch fetch all user defaults
    const defaults = await getUserDefaults([
      RESTAURANT_KEY,
      SHOW_EX_VAT_KEY,
      SHOW_EX_VAT_ORDERS_KEY,
      APP_PAGES_EXPANDED_KEY,
      LEAKAGE_QTY_UNIT_KEY
    ]);

    // Process VAT settings
    const showExVat = defaults[SHOW_EX_VAT_KEY] !== 'false'; // Default to true (excl VAT) if not set
    const showExVatOrders = defaults[SHOW_EX_VAT_ORDERS_KEY] !== 'false'; // Default to true (excl VAT) if not set
    const appPagesExpanded = defaults[APP_PAGES_EXPANDED_KEY] === 'true'; // Default to false (collapsed) if not set
    const leakageQtyUnit = parseLeakageQtyUnit(defaults[LEAKAGE_QTY_UNIT_KEY]); // Default to 'std' (kg / L / pcs)

    useAppUserDefaultStore.setState({
      showExVat,
      isVatSettingLoaded: true,
      showExVatOrders,
      isVatOrdersSettingLoaded: true,
      appPagesExpanded,
      isAppPagesExpandedLoaded: true,
      leakageQtyUnit,
      isLeakageQtyUnitLoaded: true,
      isLoading: false,
      isLoaded: true
    });

    // Process restaurant selection
    const hasMultipleRestaurants = restaurants.length >= 2;
    const allRestaurantsOption = hasMultipleRestaurants ? createAllRestaurantsOption(userId) : null;
    const selectOptions = hasMultipleRestaurants && allRestaurantsOption
      ? [allRestaurantsOption, ...restaurants]
      : restaurants;

    let selectedRestaurant;

    // While impersonating, the locally remembered pick wins over the account's
    // stored default (and is never written back to it).
    const storedRestaurantId = getImpersonationRestaurantId() ?? defaults[RESTAURANT_KEY];

    // Check if saved value is "All Restaurants"
    if (storedRestaurantId === ALL_RESTAURANTS_ID && allRestaurantsOption) {
      selectedRestaurant = allRestaurantsOption;
    } else {
      // Find restaurant by ID, fallback to first option
      selectedRestaurant = storedRestaurantId && selectOptions.find(r => r.id === storedRestaurantId)
        ? selectOptions.find(r => r.id === storedRestaurantId)!
        : selectOptions[0];
    }

    useAppUserDefaultStore.getState().setRestaurant(selectedRestaurant);

    // Save if it's different from stored value — never while impersonating, that
    // would swap the restaurant under the real user of the account.
    if (!isImpersonating() && (!defaults[RESTAURANT_KEY] || defaults[RESTAURANT_KEY] !== selectedRestaurant.id)) {
      await setUserDefault(RESTAURANT_KEY, selectedRestaurant.id);
    }

  } catch (error) {
    console.warn('Failed to load initial user defaults, using defaults');

    // Fallback to safe defaults
    useAppUserDefaultStore.setState({
      showExVat: true, // Default to excl VAT for revenue
      isVatSettingLoaded: true,
      showExVatOrders: true, // Default to excl VAT for orders
      isVatOrdersSettingLoaded: true,
      appPagesExpanded: false, // Default to collapsed
      isAppPagesExpandedLoaded: true,
      leakageQtyUnit: 'std', // Default to standardised base units on the leakage tab
      isLeakageQtyUnitLoaded: true,
      isLoading: false,
      isLoaded: true
    });

    useAppUserDefaultStore.getState().setRestaurant(restaurants[0]);
  }
}
